// src/components/BotSettings/BotSettingsScript.js
// @flow

import React, { useState, useEffect } from 'react';
import * as R from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';

import SPBackend, { BOT_SETTINGS_EVENTS } from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';
import { useI18n } from '../../contexts/I18nContext';
import { selectUser } from '../../reduxStore/modules/user';

import botSettingsScriptStyles from './Styles/BotSettingsScript.Style';

// If you have special Flow types:
type BotSettingsScriptStep = {
  en: string,
  triggerEvents?: Array<string>
};

type Props = {
  botScriptData: any,                 // The data object from the parent
  setBotScriptData: (data: any) => void
};

const StepMetadata = ({ scriptStep, classes, stepIndex }) => {
  return (
    <Typography sx={classes.stepTitle} variant='h6' component='span'>
      <b>{`Step ${stepIndex}`}</b>
    </Typography>
  );
};

const BotSettingsScript = (props: Props) => {
  const { botScriptData, setBotScriptData } = props;

  // MUI & Redux
  const theme = useTheme();
  const dispatch = useDispatch();
  const i18n = useI18n();
  const user = useSelector(selectUser);

  // Build a small “classes” object from our style file for convenience:
  const classes = {
    cardCtr: botSettingsScriptStyles.cardCtr(theme),
    textFieldCtr: botSettingsScriptStyles.textFieldCtr(theme),
    textField: botSettingsScriptStyles.textField(theme),
    stepTitle: botSettingsScriptStyles.stepTitle(theme),
    chip: botSettingsScriptStyles.chip(theme),
    submitBtn: botSettingsScriptStyles.submitBtn(theme),
    knownScriptCtr: botSettingsScriptStyles.knownScriptCtr(theme)
  };

  // Local state
  const [isBtnKnownLoading, setIsBtnKnownLoading] = useState(false);
  const [isBtnUnknownLoading, setIsBtnUnknownLoading] = useState(false);
  const [isKnownTenantScriptChanged, setIsKnownTenantScriptChanged] = useState(false);
  const [isUnknownTenantScriptChanged, setIsUnknownTenantScriptChanged] = useState(false);

  const [botSettings, setBotSettings] = useState(null);
  const [knownTenantScript, setKnownTenantScript] = useState(null);
  const [unknownTenantScript, setUnknownTenantScript] = useState(null);

  // On mount or if botScriptData changes, load the script arrays
  useEffect(() => {
    if (botScriptData && !isUnknownTenantScriptChanged && !isKnownTenantScriptChanged) {
      log('[BotSettingsScript] loading data => ', botScriptData);
      setBotSettings(botScriptData);

      // Typically your data is: { Script: { KnownTenant: [...], UnknownTenant: [...] } }
      const { KnownTenant, UnknownTenant } = botScriptData.Script || {};
      setKnownTenantScript(KnownTenant);
      setUnknownTenantScript(UnknownTenant);
    }
  }, [botScriptData, isUnknownTenantScriptChanged, isKnownTenantScriptChanged]);

  /**
   * handleSaveSettings - Called on form submit for UnknownTenant or KnownTenant
   */
  const handleSaveSettings = (nodeType = 'UnknownTenant') => (e) => {
    e.preventDefault();

    const newScriptSettings = {
      ...botSettings,
      Script: {
        ...botSettings.Script,
        [nodeType]: nodeType === 'UnknownTenant' ? unknownTenantScript : knownTenantScript
      }
    };
    log('[handleSaveSettings] nodeType, newScriptSettings:', nodeType, newScriptSettings);

    if (nodeType === 'UnknownTenant') {
      setIsBtnUnknownLoading(true);
    } else {
      setIsBtnKnownLoading(true);
    }

    SPBackend.setAccountBotSettings(user, newScriptSettings.Script)
      .then((response) => {
        log('[setAccountBotSettings] success: ', response.data);
        const { message, success } = response.data;

        // Reset flags
        if (nodeType === 'UnknownTenant') {
          setIsUnknownTenantScriptChanged(false);
          setIsBtnUnknownLoading(false);
        } else {
          setIsKnownTenantScriptChanged(false);
          setIsBtnKnownLoading(false);
        }

        // Update state & inform parent
        setBotSettings(newScriptSettings);
        setBotScriptData(newScriptSettings);

        // Show snack message
        dispatch({
          type: 'ACTION_SET_SNACK_MSG',
          snackMessage: {
            message,
            level: success,
            duration: 5000
          }
        });
      })
      .catch((err) => {
        log('[setAccountBotSettings] error: ', err);
        if (nodeType === 'UnknownTenant') {
          setIsUnknownTenantScriptChanged(false);
          setIsBtnUnknownLoading(false);
        } else {
          setIsKnownTenantScriptChanged(false);
          setIsBtnKnownLoading(false);
        }

        dispatch({
          type: 'ACTION_SET_SNACK_MSG',
          snackMessage: {
            message: 'An error occurred, and your changes were not saved. Please try again.',
            level: 'error',
            duration: 5000
          }
        });
      });
  };

  /**
   * handleStepChange - Called whenever user updates a text field for a particular script step
   */
  const handleStepChange = (nodeType, scriptStep, stepIndex) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    const newValue = e.target.value;

    // Decide which script array we’re updating
    let script;
    if (nodeType === 'UnknownTenant') {
      script = unknownTenantScript;
      setIsUnknownTenantScriptChanged(true);
    } else {
      script = knownTenantScript;
      setIsKnownTenantScriptChanged(true);
    }

    // Build a new array with updated "en" field
    const newArr = [...script];
    newArr[stepIndex] = {
      ...newArr[stepIndex],
      en: newValue
    };

    // Put it back
    if (nodeType === 'UnknownTenant') {
      setUnknownTenantScript(newArr);
    } else {
      setKnownTenantScript(newArr);
    }
  };

  return (
    <div>
      {/* UNKOWN TENANT SCRIPT */}
      {unknownTenantScript && (
        <Grid item xs={12}>
          <Typography variant='h3' component='span'>
            {'Script for unknown numbers'}
          </Typography>
          <form onSubmit={handleSaveSettings('UnknownTenant')}>
            {unknownTenantScript.map((scriptStep, index) => (
              <div key={`unknown-step-${index}`}>
                {/* show any chips for triggered events */}
                <div>
                  {scriptStep.triggerEvents?.map((event, i) =>
                    event !== '' && event !== 'set_as_ticket_name' && event !== 'create_ticket' ? (
                      <Chip
                        key={`chip-${i}`}
                        sx={classes.chip}
                        label={BOT_SETTINGS_EVENTS[event]}
                      />
                    ) : (
                      <div key={`chip-${i}`} style={{ height: 4, display: 'block' }}>
                        &nbsp;
                      </div>
                    )
                  )}
                </div>

                <div style={classes.textFieldCtr}>
                  <StepMetadata
                    scriptStep={scriptStep}
                    stepIndex={index + 1}
                    classes={classes}
                  />
                  <TextField
                    autoFocus={index === 0}
                    style={classes.textField}
                    fullWidth
                    multiline
                    maxRows={5}
                    label='Reply'
                    value={scriptStep.en}
                    onChange={handleStepChange('UnknownTenant', scriptStep, index)}
                  />
                </div>
              </div>
            ))}
            <Button
              sx={classes.submitBtn}
              color='primary'
              disabled={!isUnknownTenantScriptChanged || isBtnUnknownLoading}
              type='submit'
              variant='contained'
            >
              {'Save'}
            </Button>
          </form>
        </Grid>
      )}

      {/* KNOWN TENANT SCRIPT (optional) - DISABLED TO MATCH PRODUCTION */}
      {/* {knownTenantScript && (
        <Grid item xs={12} sx={classes.knownScriptCtr}>
          <Typography variant='h3' component='span'>
            {'Script for customers'}
          </Typography>
          <br />
          <Typography variant='body1' component='span'>
            {'After a conversation is closed, customize what the bot says to greet this customer back:'}
          </Typography>
          <form onSubmit={handleSaveSettings('KnownTenant')}>
            {knownTenantScript.map((scriptStep, index) => (
              <div key={`known-step-${index}`}>
                <div>
                  // If you need chips here, handle them similarly
                </div>
                <div style={classes.textFieldCtr}>
                  <StepMetadata
                    scriptStep={scriptStep}
                    stepIndex={index + 1}
                    classes={classes}
                  />
                  <TextField
                    style={classes.textField}
                    fullWidth
                    multiline
                    maxRows={5}
                    label='Reply'
                    value={scriptStep.en}
                    onChange={handleStepChange('KnownTenant', scriptStep, index)}
                  />
                </div>
              </div>
            ))}
            <Button
              sx={classes.submitBtn}
              color='primary'
              disabled={!isKnownTenantScriptChanged || isBtnKnownLoading}
              type='submit'
              variant='contained'
            >
              {'Save'}
            </Button>
          </form>
        </Grid>
      )} */}
    </div>
  );
};

export default BotSettingsScript;